import { setPageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";

const EMAIL = "alex@wvestudios.co.uk";

const Field = ({ label, testId, ...props }) => (
  <label className="block">
    <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#555555]">{label}</span>
    {props.rows ? (
      <textarea data-testid={testId} className="mt-2 w-full border border-black/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-[#E80042] transition-colors duration-200 resize-none" {...props} />
    ) : (
      <input data-testid={testId} className="mt-2 w-full border border-black/15 rounded-lg px-4 py-3 bg-white focus:outline-none focus:border-[#E80042] transition-colors duration-200" {...props} />
    )}
  </label>
);

const details = [
  { label: "Email", value: EMAIL, href: `mailto:${EMAIL}`, testId: "contact-email-link" },
  { label: "Phone", value: "01277 280 512", href: "tel:01277280512", testId: "contact-phone-link" },
  { label: "Studio", value: "Unit 6 Hodgson Court, Hodgson Way, Wickford, Essex, SS11 8XR", testId: "contact-address" },
  { label: "Group", value: "Part of WVE Group ↗", href: "https://www.wvegroup.co.uk/", testId: "contact-group-link", external: true },
];

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [status, setStatus] = useState("idle");
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  useEffect(() => {
    window.scrollTo(0, 0);
    setPageMeta(
      "Contact WVE Studios — Wickford, Essex",
      "Get in touch with WVE Studios: call 01277 280 512, email alex@wvestudios.co.uk or send an enquiry. Unit 6 Hodgson Court, Hodgson Way, Wickford, Essex, SS11 8XR.",
      "/contact"
    );
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/enquiries`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          message: form.message,
          context: "General Enquiry",
          extras: form.subject ? { "What's it about?": form.subject } : {},
        }),
      });
      if (!res.ok) throw new Error("failed");
      setStatus("sent");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  return (
    <main data-testid="contact-page">
      <section className="bg-[#0A0A0A] text-white pt-32 sm:pt-40 pb-20 relative overflow-hidden">
        <span className="absolute -right-16 top-0 font-display text-[20rem] leading-none text-white/[0.05] select-none hidden lg:block">HEY</span>
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 relative">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <p className="text-sm uppercase tracking-[0.3em] font-semibold text-[#E80042] mb-4">Contact</p>
            <h1 className="font-display uppercase leading-[0.95] text-5xl sm:text-7xl lg:text-8xl tracking-tight">
              Let's make<br /><span className="text-[#E80042]">something loud.</span>
            </h1>
            <p className="mt-8 text-lg sm:text-xl text-white/70 max-w-2xl leading-relaxed">
              Got a project, a date, or just an idea? Call, email or drop your details below — we'd love to hear about it.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 sm:py-28">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.15 }} className="lg:col-span-5 space-y-8">
            {details.map((d) => (
              <div key={d.label} className="border-b border-black/10 pb-6">
                <p className="text-xs uppercase tracking-[0.3em] text-[#555555] mb-2">{d.label}</p>
                {d.href ? (
                  <a
                    href={d.href}
                    data-testid={d.testId}
                    {...(d.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                    className="font-grotesk font-bold text-xl sm:text-2xl hover:text-[#E80042] transition-colors duration-200"
                  >
                    {d.value}
                  </a>
                ) : (
                  <p data-testid={d.testId} className="font-grotesk font-bold text-xl sm:text-2xl max-w-sm">{d.value}</p>
                )}
              </div>
            ))}
            <div className="rounded-lg overflow-hidden -rotate-1">
              <img
                src="https://static.wixstatic.com/media/1fe187_1bff1689ce7149e283c29ddc48e0cadf~mv2.jpg/v1/fill/w_800,h_500,al_c,q_85/desk.jpg"
                alt="WVE Studios production desk"
                className="w-full h-[280px] object-cover"
              />
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.25 }} className="lg:col-span-7">
            <div className="bg-white border border-black/10 rounded-lg p-8 sm:p-10">
              <h2 className="font-display uppercase text-3xl sm:text-4xl tracking-tight mb-2">Send an enquiry</h2>
              <p className="text-[#555555] mb-8">Tell us about your project and we'll get back to you.</p>
              <form onSubmit={submit} className="space-y-5" data-testid="contact-form">
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Your name" testId="contact-name-input" type="text" required value={form.name} onChange={set("name")} placeholder="Jane Smith" />
                  <Field label="Email" testId="contact-email-input" type="email" required value={form.email} onChange={set("email")} placeholder="you@email.com" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Mobile" testId="contact-phone-input" type="tel" value={form.phone} onChange={set("phone")} placeholder="Your mobile number" />
                  <Field label="What's it about?" testId="contact-subject-input" type="text" value={form.subject} onChange={set("subject")} placeholder="e.g. Live stream, showreel, event…" />
                </div>
                <Field label="Tell us about your project" testId="contact-message-input" rows={6} required value={form.message} onChange={set("message")} placeholder="Dates, venue, ideas — anything helps!" />
                {status === "sent" ? (
                  <div className="text-center py-6" data-testid="contact-success">
                    <span className="inline-flex w-14 h-14 rounded-full bg-[#E80042] text-white items-center justify-center text-2xl mb-4">✓</span>
                    <p className="font-grotesk font-bold text-xl">Enquiry sent — we'll be in touch shortly!</p>
                  </div>
                ) : (
                  <button
                    type="submit"
                    disabled={status === "sending"}
                    data-testid="contact-submit-button"
                    className="w-full bg-[#E80042] text-white font-grotesk font-bold text-lg py-4 rounded-full hover:bg-[#0A0A0A] transition-colors duration-300 active:scale-[0.98] disabled:opacity-60"
                  >
                    {status === "sending" ? "Sending…" : "Send Enquiry →"}
                  </button>
                )}
                {status === "error" && (
                  <p className="text-sm text-[#E80042] text-center" data-testid="contact-error">Something went wrong — please try again or call 01277 280 512.</p>
                )}
                <p className="text-xs text-[#555555] text-center">Goes straight to {EMAIL}</p>
              </form>
            </div>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
